import { apiClient } from './apiClient';

/**
 * Provenance information for a single field in a device's effective_config.
 * Produced by the backend config assembly pipeline.
 */
export interface ConfigSourceInfo {
  layer: 'hardware_default' | 'device_column' | 'template' | 'device_override';
  template_id: number | null;
  template_name: string | null;
  priority: number | null;
}

export interface DeviceDetail {
  idDevice: string;
  idClient: string | null;
  syslog_host: string;
  syslog_port: number;
  syslog_facility: string;
  provisioning: string;
  keepalive_timer: number;
  password: string;
  mqtt_host: string;
  mqtt_port: number;
  mqtt_user: string;
  mqtt_pass: string;
  conf47Type: string;
  conf47Type_source: 'template' | 'manual' | null;  // Source of conf47Type: 'template' (derived) or 'manual' (legacy)
  date_time: string;
  deviceType: string;
  mqtt_voice_dsts: string[] | null;
  jsonAdditionalConfig: Record<string, unknown> | null;
  phone: string;
  lastUpdateDateTime: string;
  needsRefresh: boolean;

  // Template-related fields
  template_id: number | null;
  template_name: string | null;
  max_template_priority: number | null;
  config_overridden: boolean;
  config_override_json: Record<string, unknown> | null;
  template_applied_at: string | null;

  // Computed effective config
  effective_config: Record<string, unknown>;

  // New additive fields from the config assembly pipeline
  effective_custom_fields?: Array<Record<string, unknown>>;
  config_sources?: Record<string, ConfigSourceInfo>;
  applied_template_ids?: number[];
  last_request_at?: string | null;
  last_request_by?: string | null;

  // ── Config chain cache metadata ────────────────────────────────
  //
  // These three fields are populated by the backend's cached-assembly
  // layer the first time a device polls /v1/conf or /v2/conf. Before
  // the first poll they are all null. They provide:
  //
  // - config_hash: SHA-256 hex digest of the canonical JSON of the
  //   assembled effective_config + effective_custom_fields. This is
  //   the same value the firmware receives as the HTTP ETag header.
  //   The Device Detail modal shows the first 12 chars of the hash
  //   so admins can see at a glance whether the config has changed
  //   between two views without diffing the full payload.
  //
  // - config_generated_at: wall-clock timestamp of when the current
  //   config_hash was produced. Always <= the last poll time for
  //   this device. If this lags far behind lastUpdateDateTime (for
  //   instance after a mass template update) it means the device
  //   hasn't polled since the change — useful for debugging
  //   "why does this device still have the old config".
  //
  // - last_config_audit_at: when the most recent
  //   "device.config.regenerated" audit row was written for this
  //   device. Driven by the 60s debounce window on the backend.
  config_hash?: string | null;
  config_generated_at?: string | null;
  last_config_audit_at?: string | null;
}

export interface TemplateForDevice {
  id: number;
  name: string;
  description: string | null;
  device_type: string;
  priority: number;
  client_id: string;
}

export interface DeviceFirmwareVersion {
  id: number;
  device_id: string;
  component_type: string;  // MAIN, WWWDATA, GSM, MODEM, etc.
  version: string;
  last_check: string | null;
  last_update: string | null;
}

class DeviceService {
  /**
   * Get detailed device information including template data
   */
  async getDeviceDetail(deviceId: string): Promise<DeviceDetail> {
    return await apiClient.get(`/v2/conf/${deviceId}/detail`);
  }

  /**
   * Patch device fields (e.g. max_template_priority)
   */
  async patchDevice(deviceId: string, data: Record<string, unknown>): Promise<unknown> {
    return await apiClient.patch(`/v2/conf/${deviceId}`, data);
  }

  /**
   * Apply a config template to a device
   */
  async applyTemplate(deviceId: string, templateId: number): Promise<DeviceDetail> {
    return await apiClient.post(`/v2/conf/${deviceId}/apply-template`, {
      template_id: templateId
    });
  }

  /**
   * Override specific configuration values for a device
   */
  async overrideConfig(deviceId: string, overrides: Record<string, unknown>): Promise<DeviceDetail> {
    return await apiClient.post(`/v2/conf/${deviceId}/override-config`, {
      overrides
    });
  }

  /**
   * Reset device configuration to match the linked template
   */
  async resetToTemplate(deviceId: string): Promise<DeviceDetail> {
    return await apiClient.post(`/v2/conf/${deviceId}/reset-to-template`, {});
  }

  /**
   * Get available templates for a device
   */
  async getTemplatesForDevice(deviceId: string): Promise<TemplateForDevice[]> {
    return await apiClient.get(`/v2/conf/${deviceId}/templates`);
  }

  /**
   * Get firmware versions for a device
   */
  async getDeviceFirmwareVersions(deviceId: string): Promise<DeviceFirmwareVersion[]> {
    return await apiClient.get(`/v2/device-versions?device_id=${deviceId}`);
  }
}

export const deviceService = new DeviceService();
